import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { KeyRound, Shield, ShieldCheck, ShieldOff, Copy, AlertTriangle, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin → Security / 2FA settings page.
 *
 * Three visual states:
 *  1. Disabled - show "Enable" CTA which starts the enrolment flow
 *  2. Enrolling - show QR code + manual secret + verify-code form
 *  3. Enabled - show "Disable" and "Regenerate recovery codes" forms
 *
 * Recovery codes are shown ONCE right after enrol/regenerate; after that
 * the plaintext is discarded on the server so we can't re-surface them.
 */
export default function AdminSecurity() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  // Enrolment state - non-null when we're between /setup and /verify-setup
  const [enroll, setEnroll] = useState(null); // { secret, qr_svg }
  const [enrollCode, setEnrollCode] = useState("");
  const [busy, setBusy] = useState(false);
  // Recovery codes are only in state when we've JUST generated them.
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [ackRecovery, setAckRecovery] = useState(false);
  // Disable form
  const [disablePwd, setDisablePwd] = useState("");
  const [disableCode, setDisableCode] = useState("");
  // Regenerate form
  const [regenCode, setRegenCode] = useState("");

  useEffect(() => {
    api
      .get("/auth/2fa/status")
      .then((r) => setEnabled(Boolean(r.data.enabled)))
      .finally(() => setLoading(false));
  }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setEnroll({ secret: data.secret, qr_svg: data.qr_svg });
      setEnrollCode("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not start 2FA setup.");
    } finally {
      setBusy(false);
    }
  };

  const cancelEnroll = () => {
    setEnroll(null);
    setEnrollCode("");
  };

  const submitEnroll = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/verify-setup", { code: enrollCode });
      setRecoveryCodes(data.recovery_codes);
      setAckRecovery(false);
      setEnabled(true);
      setEnroll(null);
      setEnrollCode("");
      await refreshUser();
      toast.success("Two-factor authentication is now on.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Verification failed.");
    } finally {
      setBusy(false);
    }
  };

  const submitDisable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd, code: disableCode });
      setEnabled(false);
      setDisablePwd("");
      setDisableCode("");
      setRecoveryCodes(null);
      await refreshUser();
      toast.success("Two-factor authentication disabled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not disable 2FA.");
    } finally {
      setBusy(false);
    }
  };

  const submitRegenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-codes", { code: regenCode });
      setRecoveryCodes(data.recovery_codes);
      setAckRecovery(false);
      setRegenCode("");
      toast.success("New recovery codes generated. Save them now.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not regenerate codes.");
    } finally {
      setBusy(false);
    }
  };

  const copyAllCodes = () => {
    if (!recoveryCodes) return;
    navigator.clipboard
      .writeText(recoveryCodes.join("\n"))
      .then(() => toast.success("Recovery codes copied to clipboard."))
      .catch(() => toast.error("Copy failed. Please select and copy manually."));
  };

  if (loading) return <div className="p-10 text-center text-[#447694]">Loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-3xl" data-testid="admin-security-page">
      <span className="bnp-chip bnp-chip-light">Security</span>
      <h1 className="font-display text-3xl md:text-4xl text-[#04273d] mt-2 font-semibold tracking-tight">
        Two-factor authentication
      </h1>
      <p className="text-sm text-[#447694] mt-1">
        Add a second step to sign-in using an authenticator app (Google Authenticator,
        Authy, 1Password, Microsoft Authenticator or similar).
      </p>

      {/* State pill */}
      <div className="mt-4">
        {enabled ? (
          <span className="bnp-chip bnp-chip-teal" data-testid="admin-2fa-enabled-chip">
            <ShieldCheck size={12} /> Enabled for {user?.email}
          </span>
        ) : (
          <span className="bnp-chip bnp-chip-light" data-testid="admin-2fa-disabled-chip">
            <ShieldOff size={12} /> Not enabled for {user?.email}
          </span>
        )}
      </div>

      {/* One-time recovery-code display - appears after enrol OR regenerate */}
      {recoveryCodes && (
        <div className="bnp-card mt-6 p-6 border-2 border-[#71edc9]" data-testid="admin-2fa-recovery-panel">
          <div className="flex items-start gap-3">
            <AlertTriangle size={22} className="text-[#a55a2d] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold text-[#04273d]">Save these recovery codes now</p>
              <p className="text-sm text-[#447694] mt-1 leading-relaxed">
                Each code works exactly once. If you ever lose access to your authenticator app,
                you can use one of these to sign in. We can&rsquo;t show them again after you leave
                this page.
              </p>
            </div>
          </div>

          <ul className="mt-4 grid grid-cols-2 sm:grid-cols-2 gap-2 font-mono-brand text-sm text-[#04273d]" data-testid="admin-2fa-recovery-list">
            {recoveryCodes.map((c) => (
              <li key={c} className="bg-[#f7f7f7] rounded-xl border border-[#9ac7f7]/30 px-3 py-2 text-center tracking-wider" data-testid={`admin-2fa-recovery-code-${c}`}>
                {c}
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={copyAllCodes}
              className="btn-secondary text-xs"
              data-testid="admin-2fa-copy-codes-button"
            >
              <Copy size={12} /> Copy all
            </button>
            <label className="flex items-center gap-2 text-xs text-[#04273d] cursor-pointer ml-auto">
              <input
                type="checkbox"
                checked={ackRecovery}
                onChange={(e) => setAckRecovery(e.target.checked)}
                className="h-4 w-4 accent-[#04273d]"
                data-testid="admin-2fa-ack-checkbox"
              />
              I&rsquo;ve saved these codes somewhere safe
            </label>
            <button
              type="button"
              onClick={() => setRecoveryCodes(null)}
              disabled={!ackRecovery}
              className="btn-primary text-xs"
              data-testid="admin-2fa-dismiss-codes-button"
            >
              <Check size={12} /> Done
            </button>
          </div>
        </div>
      )}

      {/* DISABLED - show enrol CTA */}
      {!enabled && !enroll && !recoveryCodes && (
        <div className="bnp-card mt-6 p-6" data-testid="admin-2fa-enroll-cta">
          <div className="flex items-start gap-3">
            <Shield size={20} className="text-[#447694] mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-display font-semibold text-[#04273d]">Turn on two-factor authentication</p>
              <p className="text-sm text-[#447694] mt-1 leading-relaxed">
                Once enabled, every future sign-in will ask for a 6-digit code from your
                authenticator app in addition to your password. Setup takes about a minute.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={startEnroll}
            disabled={busy}
            className="btn-primary mt-4"
            data-testid="admin-2fa-start-button"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            {busy ? "Preparing…" : "Enable 2FA"}
          </button>
        </div>
      )}

      {/* ENROLLING - show QR + verify form */}
      {enroll && (
        <div className="bnp-card mt-6 p-6 space-y-5" data-testid="admin-2fa-enroll-panel">
          <div>
            <p className="font-display font-semibold text-[#04273d]">1. Scan this QR code</p>
            <p className="text-sm text-[#447694] mt-1 leading-relaxed">
              Open your authenticator app, add a new account and scan the code below.
            </p>
            <div
              className="mt-3 bg-white border border-[#9ac7f7]/30 rounded-2xl p-4 inline-block"
              data-testid="admin-2fa-qr"
              dangerouslySetInnerHTML={{ __html: enroll.qr_svg.replace(/<svg /, '<svg width="200" height="200" ') }}
            />
          </div>

          <div>
            <p className="font-display font-semibold text-[#04273d] text-sm">Can&rsquo;t scan?</p>
            <p className="text-xs text-[#447694] mt-1">Type this key manually into your app instead.</p>
            <code
              className="mt-2 block bg-[#f7f7f7] border border-[#9ac7f7]/30 rounded-xl px-3 py-2 font-mono-brand text-sm text-[#04273d] tracking-wider break-all"
              data-testid="admin-2fa-secret"
            >
              {enroll.secret}
            </code>
          </div>

          <form onSubmit={submitEnroll} className="space-y-3" data-testid="admin-2fa-enroll-form">
            <div>
              <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">
                2. Enter the 6-digit code from your app
              </label>
              <input
                type="text"
                required
                autoFocus
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value)}
                className="bnp-input font-mono-brand tracking-[0.3em] text-center text-lg"
                placeholder="123 456"
                inputMode="numeric"
                autoComplete="one-time-code"
                data-testid="admin-2fa-enroll-code-input"
                maxLength={8}
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="submit" disabled={busy || enrollCode.length < 6} className="btn-primary text-sm" data-testid="admin-2fa-enroll-submit-button">
                <ShieldCheck size={14} /> {busy ? "Verifying…" : "Verify and enable"}
              </button>
              <button type="button" onClick={cancelEnroll} className="btn-ghost text-sm" data-testid="admin-2fa-enroll-cancel-button">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ENABLED - offer regenerate + disable */}
      {enabled && !recoveryCodes && (
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {/* Regenerate recovery codes */}
          <div className="bnp-card p-6" data-testid="admin-2fa-regenerate-card">
            <p className="font-display font-semibold text-[#04273d]">Regenerate recovery codes</p>
            <p className="text-sm text-[#447694] mt-1 leading-relaxed">
              Invalidates your current recovery codes and issues 10 fresh ones. Use this if
              you suspect the old set is compromised or you&rsquo;ve run out.
            </p>
            <form onSubmit={submitRegenerate} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">
                  Authenticator code
                </label>
                <input
                  type="text"
                  required
                  value={regenCode}
                  onChange={(e) => setRegenCode(e.target.value)}
                  className="bnp-input font-mono-brand tracking-wider text-center"
                  placeholder="123456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  data-testid="admin-2fa-regenerate-code-input"
                  maxLength={8}
                />
              </div>
              <button type="submit" disabled={busy || regenCode.length < 6} className="btn-secondary text-sm w-full justify-center" data-testid="admin-2fa-regenerate-submit-button">
                <KeyRound size={14} /> {busy ? "Regenerating…" : "Regenerate codes"}
              </button>
            </form>
          </div>

          {/* Disable */}
          <div className="bnp-card p-6" data-testid="admin-2fa-disable-card">
            <p className="font-display font-semibold text-[#04273d]">Disable two-factor</p>
            <p className="text-sm text-[#447694] mt-1 leading-relaxed">
              Turns 2FA off for this account. You&rsquo;ll be able to sign in with just your
              password again. Requires your password AND a fresh authenticator code (or a
              recovery code) so a stolen session alone can&rsquo;t remove protection.
            </p>
            <form onSubmit={submitDisable} className="mt-4 space-y-3">
              <div>
                <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={disablePwd}
                  onChange={(e) => setDisablePwd(e.target.value)}
                  className="bnp-input"
                  autoComplete="current-password"
                  data-testid="admin-2fa-disable-password-input"
                />
              </div>
              <div>
                <label className="block text-xs font-mono-brand tracking-wider uppercase text-[#447694] mb-1.5">
                  Authenticator code (or recovery code)
                </label>
                <input
                  type="text"
                  required
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  className="bnp-input font-mono-brand tracking-wider text-center"
                  placeholder="123456"
                  autoComplete="one-time-code"
                  data-testid="admin-2fa-disable-code-input"
                  maxLength={15}
                />
              </div>
              <button type="submit" disabled={busy || !disablePwd || disableCode.length < 6} className="btn-ghost text-sm w-full justify-center text-red-700 hover:bg-red-50" data-testid="admin-2fa-disable-submit-button">
                <ShieldOff size={14} /> {busy ? "Disabling…" : "Disable 2FA"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
